import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Card,
    CardContent,
    Paper,
    Chip,
    Avatar,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BoltIcon from '@mui/icons-material/Bolt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonIcon from '@mui/icons-material/Person';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import KitchenIcon from '@mui/icons-material/Kitchen';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';

import ReactECharts from 'echarts-for-react';
import DeviceConsumptionCard from '../../components/devices/DeviceConsumptionCard';
import { ROOM_DEVICES, getRoomsOrder, getRoomDevicesForApartment } from '../../data/mockApartmentDevices';
import { getAllHouses } from '../../api_calls/api_calls_houses';
import { BUILDING_ADDRESS, DEMO_TODAY, mapHouseToApartment } from '../../data/mockApartments';
import { useUserContext } from '../../context/UserContext';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { multilingual } from '../../multilingual';

const formatDate = (dateStr, locale = 'en-GB') => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOccupied = (checkIn, checkOut) => {
    const now = new Date(DEMO_TODAY);
    now.setHours(0, 0, 0, 0);
    const in_ = new Date(checkIn);
    in_.setHours(0, 0, 0, 0);
    const out = new Date(checkOut);
    out.setHours(23, 59, 59, 999);
    return now >= in_ && now <= out;
};

// Compute per-room total consumption from device data (sum of last 7 days per device).
const getRoomTotals = (roomDevices = ROOM_DEVICES) => {
    const totals = {};
    getRoomsOrder().forEach((roomKey) => {
        const room = roomDevices[roomKey];
        if (!room?.devices) return;
        totals[roomKey] = room.devices.reduce((roomSum, device) => {
            const deviceTotal = (device.consumption || []).reduce((s, v) => s + (Number(v) || 0), 0);
            return roomSum + deviceTotal;
        }, 0);
    });
    return totals;
};

// Room totals and Other so they sum to apartment total. Scale room totals when apartment total < raw sum.
const getRoomTotalsAndOther = (apartmentTotal, roomDevices = ROOM_DEVICES) => {
    const raw = getRoomTotals(roomDevices);
    const totalFromRooms = Object.values(raw).reduce((s, v) => s + v, 0);
    if (totalFromRooms <= 0) {
        const perRoom = apartmentTotal / 5;
        return { roomTotals: { livingRoom: perRoom, kitchen: perRoom, bedroom: perRoom, bathroom: perRoom }, other: perRoom };
    }
    let roomTotals;
    let other;
    if (apartmentTotal >= totalFromRooms) {
        roomTotals = { ...raw };
        other = Math.max(0, apartmentTotal - totalFromRooms);
    } else {
        const scale = apartmentTotal / totalFromRooms;
        roomTotals = {};
        getRoomsOrder().forEach((key) => { roomTotals[key] = (raw[key] || 0) * scale; });
        other = 0;
    }
    return { roomTotals, other };
};

const ROOM_ICONS = {
    livingRoom: AcUnitIcon,
    kitchen: KitchenIcon,
    bedroom: BedIcon,
    bathroom: BathtubIcon,
};

const ROOM_TILE_STYLE = {
    livingRoom: {
        bg: 'linear-gradient(145deg, #e3f2fd 0%, #bbdefb 100%)',
        border: '1px solid rgba(25, 118, 210, 0.2)',
        iconColor: '#1565c0',
    },
    kitchen: {
        bg: 'linear-gradient(145deg, #fff8e1 0%, #ffecb3 100%)',
        border: '1px solid rgba(255, 152, 0, 0.25)',
        iconColor: '#e65100',
    },
    bedroom: {
        bg: 'linear-gradient(145deg, #f3e5f5 0%, #e1bee7 100%)',
        border: '1px solid rgba(156, 39, 176, 0.2)',
        iconColor: '#7b1fa2',
    },
    bathroom: {
        bg: 'linear-gradient(145deg, #e0f7fa 0%, #b2ebf2 100%)',
        border: '1px solid rgba(0, 150, 167, 0.2)',
        iconColor: '#00695c',
    },
};

const renderRoomTile = (roomKey, theme, dict, ROOM_DEVICES, ROOM_ICONS, ROOM_TILE_STYLE, DeviceConsumptionCard, chartsLayout, spanTwoRows, spanTwoCols) => {
    const room = ROOM_DEVICES[roomKey];
    const RoomIcon = ROOM_ICONS[roomKey];
    const roomName = dict?.rooms?.[roomKey] ?? roomKey;
    const tileStyle = ROOM_TILE_STYLE[roomKey];
    const isRow = chartsLayout === 'row';
    return (
        <Box
            key={roomKey}
            sx={{
                minHeight: 0,
                ...(spanTwoRows && { gridRow: { md: 'span 2' } }),
                ...(spanTwoCols && { gridColumn: { md: 'span 2' } }),
                ...(spanTwoCols && { alignSelf: 'start' }),
            }}
        >
            <Card
                elevation={0}
                sx={{
                    height: spanTwoCols ? 'auto' : '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    background: tileStyle.bg,
                    border: '1px solid transparent',
                    boxShadow: 'none',
                    transition: 'border-color 0.2s ease',
                    '&:hover': {
                        borderColor: theme.palette.secondary.main,
                    },
                }}
            >
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        backgroundColor: 'rgba(255,255,255,0.4)',
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            boxShadow: 1,
                        }}
                    >
                        <RoomIcon sx={{ fontSize: 22, color: tileStyle.iconColor }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {roomName}
                    </Typography>
                </Box>
                <CardContent
                    sx={{
                        p: 2,
                        '&:last-child': { pb: 2 },
                        height: spanTwoCols ? 'auto' : '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isRow ? 'row' : 'column',
                            gap: 2,
                            flex: spanTwoCols ? '0 0 auto' : 1,
                            minHeight: 0,
                        }}
                    >
                        {room.devices.map((device) => (
                            <Box key={device.id} sx={{ flex: isRow ? 1 : '0 0 auto', minWidth: 0 }}>
                                <DeviceConsumptionCard device={device} roomName={roomName} consumptionWarning={device.id === 'ac-bedroom' || device.id === 'boiler'} />
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

const ApartmentAdministration = () => {
    const { id, buildingId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { token } = useUserContext();
    const { language } = useLanguageContext();
    const dict = multilingual[language]?.apartmentAdmin ?? multilingual.gr.apartmentAdmin;

    const [houses, setHouses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!token) {
            setHouses([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        getAllHouses(token)
            .then((data) => setHouses(Array.isArray(data) ? data : []))
            .catch(() => setHouses([]))
            .finally(() => setLoading(false));
    }, [token]);

    const apartments = houses.map(mapHouseToApartment);
    const apartment = apartments.find((a) => String(a.id) === String(id));
    const apartmentTotal = apartment?.totalConsumption ?? 0;
    const roomDevices = getRoomDevicesForApartment(apartment?.id);
    const { roomTotals, other } = getRoomTotalsAndOther(apartmentTotal, roomDevices);
    const dashboardDict = multilingual[language]?.dashboard ?? multilingual.gr.dashboard;
    const occupied = apartment ? isOccupied(apartment.checkIn, apartment.checkOut) : false;
    const dateLocale = language === 'gr' ? 'el-GR' : 'en-GB';

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ my: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, '@media (min-width: 1920px)': { maxWidth: 1536 } }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!apartment) {
        return (
            <Container maxWidth="lg" sx={{ my: 5, '@media (min-width: 1920px)': { maxWidth: 1536 } }}>
                <Typography color="text.secondary">Apartment not found.</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(buildingId ? `/building/${buildingId}` : '/')} sx={{ mt: 2 }}>
                    {dict?.backToDashboard}
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ my: 5, '@media (min-width: 1920px)': { maxWidth: 1536 } }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/building/${buildingId}`)} sx={{ textTransform: 'none', mb: 1, px: 0 }}>
                {dict.backToBuilding}
            </Button>
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    px: 3,
                    py: 3,
                    borderRadius: 5,
                    backgroundColor: theme.palette.common.white,
                    width: '100%',
                    mb: 4,
                    boxShadow: 'none',
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ mb: 1 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                background: 'linear-gradient(90deg, #1976D2 0%, #40C4FF 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <ApartmentIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                            {apartment.name}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {dict?.overview}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'stretch' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1.5,
                                borderRadius: 2,
                                bgcolor: 'grey.100',
                                width: 'fit-content',
                                minWidth: '200px',
                            }}
                        >
                            <LocationOnIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    {dict?.address}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    {BUILDING_ADDRESS}
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                gap: 1,
                                px: 2,
                                py: 1.5,
                                borderRadius: 2,
                                bgcolor: 'grey.100',
                                minWidth: 180,
                            }}
                        >
                            <Chip
                                size="small"
                                icon={occupied ? <PersonIcon sx={{ fontSize: 16, color: 'inherit !important' }} /> : <HomeOutlinedIcon sx={{ fontSize: 16, color: 'inherit !important' }} />}
                                label={occupied ? dashboardDict.occupied : dashboardDict.notOccupied}
                                sx={{
                                    bgcolor: occupied ? '#2e7d32' : '#616161',
                                    color: 'white',
                                    fontWeight: 600,
                                    width: 'fit-content',
                                    '& .MuiChip-icon': { ml: 0.5 },
                                }}
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                {occupied ? (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">{dashboardDict.currentStayDates}:</Typography>
                                            <Typography variant="body2" fontWeight={600}>{formatDate(apartment.checkIn, dateLocale)} – {formatDate(apartment.checkOut, dateLocale)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <EventAvailableIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">{dashboardDict.nextStayDates}:</Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {apartment.nextCheckIn && apartment.nextCheckOut ? `${formatDate(apartment.nextCheckIn, dateLocale)} – ${formatDate(apartment.nextCheckOut, dateLocale)}` : '—'}
                                            </Typography>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <EventIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">{dashboardDict.lastStayDates}:</Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {apartment.lastStayCheckIn && apartment.lastStayCheckOut ? `${formatDate(apartment.lastStayCheckIn, dateLocale)} – ${formatDate(apartment.lastStayCheckOut, dateLocale)}` : '—'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <EventAvailableIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="caption" color="text.secondary">{dashboardDict.nextStayDates}:</Typography>
                                            <Typography variant="body2" fontWeight={600}>{formatDate(apartment.checkIn, dateLocale)} – {formatDate(apartment.checkOut, dateLocale)}</Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                            {dict?.consumptionByRoom ?? 'Consumption by room'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, width: '100%' }}>
                            {getRoomsOrder().map((roomKey) => (
                                <Box
                                    key={roomKey}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: 1.5,
                                        bgcolor: 'grey.100',
                                        border: '1px solid',
                                        borderColor: 'grey.300',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {dict?.rooms?.[roomKey] ?? roomKey}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} color="primary.main">
                                        {(roomTotals[roomKey] ?? 0).toFixed(1)} kWh
                                    </Typography>
                                </Box>
                            ))}
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1.5,
                                    bgcolor: 'grey.200',
                                    border: '1px solid',
                                    borderColor: 'grey.400',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {dict?.rooms?.other ?? 'Other'}
                                </Typography>
                                <Typography variant="body2" fontWeight={700} color="text.primary">
                                    {other.toFixed(1)} kWh
                                </Typography>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1.5,
                                px: 2,
                                py: 1.5,
                                mt: 1.5,
                                borderRadius: 2,
                                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                color: 'white',
                                boxShadow: 1,
                                width: '100%',
                            }}
                        >
                            <BoltIcon sx={{ color: '#FFD700', fontSize: '1.75rem' }} />
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem' }}>
                                    {dict?.totalConsumption}
                                </Typography>
                                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                                    {apartmentTotal.toFixed(2)} kWh
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ flex: '0 0 420px', minWidth: 320, maxWidth: '100%' }}>
                    {(() => {
                        const labels = apartment.lastDaysLabels ?? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                        const consumption = apartment.lastDaysConsumption ?? [];
                        const apartmentCardDict = multilingual[language]?.apartmentCard ?? multilingual.gr?.apartmentCard;
                        const chartOption = {
                            xAxis: {
                                type: 'category',
                                data: labels,
                                axisLine: { show: true },
                                axisLabel: { color: '#999', fontSize: 10 },
                                axisTick: { show: false },
                            },
                            yAxis: {
                                type: 'value',
                                axisLine: { show: false },
                                axisLabel: { color: '#999', fontSize: 10 },
                                splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
                            },
                            series: [{
                                data: consumption,
                                type: 'line',
                                smooth: true,
                                areaStyle: { color: '#2196f3', opacity: 0.3 },
                                lineStyle: { color: '#2196f3', width: 2 },
                                symbol: 'none',
                            }],
                            grid: { top: 25, bottom: 35, left: 40, right: 10 },
                            tooltip: {
                                trigger: 'axis',
                                formatter: '{b}<br/>{c} kWh',
                            },
                        };
                        const estimatedCostEur = (apartmentTotal * 0.16).toFixed(2);
                        return (
                            <Card
                                elevation={0}
                                sx={{
                                    borderRadius: 5,
                                    height: '100%',
                                    boxShadow: 'none',
                                    backgroundColor: 'white',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <CardContent sx={{ padding: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Avatar sx={{ width: 36, height: 36, bgcolor: theme.palette.secondary.main }}>
                                            <ApartmentIcon sx={{ color: theme.palette.common.white, fontSize: 20 }} />
                                        </Avatar>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {apartment.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ mt: 1.5, flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {apartmentCardDict?.last7DaysConsumption ?? 'Last 7 Days Consumption (kWh)'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="subtitle2" sx={{ color: '#2196f3', mr: 0.5 }}>
                                                    {apartmentCardDict?.total ?? 'Total'}:
                                                </Typography>
                                                <Typography variant="h6" fontWeight="bold" color="#2196f3" sx={{ lineHeight: 1 }}>
                                                    {apartmentTotal.toFixed(2)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#2196f3' }}>kWh</Typography>
                                            </Box>
                                        </Box>
                                        <ReactECharts option={chartOption} style={{ height: 240, width: '100%' }} />
                                    </Box>
                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">{apartmentCardDict?.estimatedCost ?? 'Estimated cost'}</Typography>
                                        <Typography variant="body2" fontWeight={700} color="primary.dark">{estimatedCostEur} €</Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })()}
                </Box>
            </Paper>

            {/* Tile layout: Tile 1 & 2 = one room + one chart; Tile 3 = one room + two charts vertical; Tile 4 = one room + two charts horizontal */}
            <Box
                sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                    gridAutoRows: 'auto',
                }}
            >
                {renderRoomTile('livingRoom', theme, dict, roomDevices, ROOM_ICONS, ROOM_TILE_STYLE, DeviceConsumptionCard, 'column', false, false)}
                {renderRoomTile('bedroom', theme, dict, roomDevices, ROOM_ICONS, ROOM_TILE_STYLE, DeviceConsumptionCard, 'column', false, false)}
                {renderRoomTile('kitchen', theme, dict, roomDevices, ROOM_ICONS, ROOM_TILE_STYLE, DeviceConsumptionCard, 'column', true, false)}
                {renderRoomTile('bathroom', theme, dict, roomDevices, ROOM_ICONS, ROOM_TILE_STYLE, DeviceConsumptionCard, 'row', false, true)}
            </Box>
        </Container>
    );
};

export default ApartmentAdministration;
